import React, { useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import PaymentIcon from "@mui/icons-material/Payment";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GlobalContext } from "../context/global_context";
import axios from "axios";

const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText("#005cbf"),
  backgroundColor: "#606770",
  "&:hover": {
    backgroundColor: "#606770",
  },
}));

const ColorButtonSecond = styled(Button)(({ theme }) => ({
  color: "#606770",
  backgroundColor: "#ffc3d0",
  border: "none",
  "&:hover": {
    transform: "scale(1.005)",
    boxShadow: "3px 3px 6px 0px rgba(0, 0, 0, 0.2)",
  },
}));

const hasPaidReceipt = (payment) => {
  if (payment?.status !== "pagado" || typeof payment.formato !== "string" || !payment.formato.trim()) {
    return false;
  }

  try {
    const receiptUrl = new URL(payment.formato, window.location.origin);
    return receiptUrl.protocol === "http:" || receiptUrl.protocol === "https:";
  } catch {
    return false;
  }
};

const PagoLineaPublic = () => {
  const { GlobalState, setGlobalState } = useContext(GlobalContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(true);

  const lcapturaQuery = searchParams.get("lcaptura");

  useEffect(() => {
    const consultarPago = async () => {
      if (!lcapturaQuery && !GlobalState?.lineaCaptura?.linea_captura && !hasPaidReceipt(GlobalState?.lineaCaptura)) {
        setError("No se proporcionó una línea de captura válida.");
        setFetching(false);
        return;
      }

      if (lcapturaQuery) {
        try {
          setFetching(true);
          const response = await axios.get(
            `/api/pagos/consultar-lcaptura?lcaptura=${encodeURIComponent(lcapturaQuery)}`
          );

          if (response.data && response.data.estatus === 1) {
            setGlobalState((prev) => ({
              ...prev,
              lineaCaptura: response.data.data,
            }));
          } else {
            setError(response.data?.mensaje || "Orden de pago no encontrada.");
          }
        } catch (err) {
          console.error("Error consultando la línea de captura:", err);
          setError("Ocurrió un error al obtener la información del pago.");
        } finally {
          setFetching(false);
        }
      } else {
        setFetching(false);
      }
    };

    consultarPago();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lcapturaQuery]);

  const datosPago = GlobalState?.lineaCaptura || {};
  const {
    conceptoplinea = "",
    fechaplinea = "",
    formato = "",
    importe = "",
    linea_captura = "",
    status = "pendiente",
    token: TOKEN = "",
  } = datosPago;

  const pdfFake = "";

  const regresar = () => {
    if (GlobalState.openpagoLinea !== undefined) {
      setGlobalState({ ...GlobalState, openpagoLinea: false });
    }
    navigate("/mis-cursos");
  };

  if (fetching) {
    return (
      <Container
        maxWidth="md"
        sx={{
          height: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="text-center">
          <span className="loader"></span>
          <p className="mt-3">Consultando información del pago...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        maxWidth="md"
        sx={{
          height: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box className="p-4 border rounded shadow-sm text-center">
          <h4>Atención</h4>
          <p className="my-3 text-danger">{error}</p>
          <Button variant="contained" onClick={() => navigate("/")}>
            Ir al Inicio
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <div className="h-100">
      <Container maxWidth="md">
        <Box sx={{ height: "auto", paddingTop: "6%" }}>
          <Box>
            <Grid
              container
              spacing={1}
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Grid item xs="auto">
                <ColorButtonSecond
                  variant="outlined"
                  onClick={regresar}
                  endIcon={<KeyboardReturnIcon />}
                >
                  Regresar
                </ColorButtonSecond>
              </Grid>

              <Grid item xs="auto">
                <a
                  rel="noreferrer"
                  target="_blank"
                  href={formato ? `${formato}/2` : pdfFake}
                >
                  <ColorButtonSecond
                    variant="outlined"
                    endIcon={<CloudDownloadIcon />}
                  >
                    {status === "pendiente" || status === "vencido"
                      ? "Descargar mi formato de pago"
                      : "Descargar comprobante de pago"}
                  </ColorButtonSecond>
                </a>
              </Grid>

              <Grid item xs="auto">
                <form action={process.env.REACT_APP_URLPAGO} method="post">
                  <input
                    type="hidden"
                    name="concepto"
                    value={conceptoplinea}
                  />
                  <input
                    type="hidden"
                    name="lcaptura"
                    value={linea_captura}
                  />
                  <input type="hidden" name="monto" value={importe} />
                  <input
                    type="hidden"
                    name="fecha_vencimiento"
                    value={fechaplinea}
                  />
                  <input type="hidden" name="tkn" value={TOKEN} />
                  <input
                    type="hidden"
                    name="urlRetorno"
                    value={process.env.REACT_APP_URLREDIRECT}
                  />
                  <input type="hidden" name="email" value="" />
                  {status === "pendiente" && (
                    <ColorButton
                      variant="outlined"
                      type="submit"
                      endIcon={<PaymentIcon />}
                    >
                      Pagar en Línea
                    </ColorButton>
                  )}
                </form>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <div className="pt-2 border-bottom--guinda text-big mb-2 text-bold">
          {status === "pendiente" ? "Formato de pago" : "Comprobante de Pago"}
        </div>

        {/* Loader sobre el PDF */}
        <div
          className="vh100 d-flex justify-content-center align-items-center pt-3 pb-3"
          style={{ position: "relative" }}
        >
          {loadingPdf && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <span className="loader"></span>
            </div>
          )}

          <object
            data={formato}
            type="application/pdf"
            className="responsive-pdf"
            title="PDF Preview"
            onLoad={() => setLoadingPdf(false)}
          >
            <p>
              Hubo un error al mostrar el PDF. Da clic{" "}
              <a href={formato}>aquí</a> para descargar tu formato.
            </p>
          </object>
        </div>
      </Container>
    </div>
  );
};

export default PagoLineaPublic;
